"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  Grid,
  Paper,
  Stack,
  Chip,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Warning as AlertTriangleIcon,
  Close as CloseIcon,
  KeyboardArrowUp,
  KeyboardArrowDown,
} from "@mui/icons-material";
import type { Segment, WorkOrder } from "./lib/types";

interface BreakSegmentModalProps {
  segment: Segment | null;
  workOrder: WorkOrder | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (parts: number[]) => void;
  onGoOverCapacity?: () => void;
  capacityWarning?: {
    technicianName: string;
    day: string;
    availableHours: number;
    requestedHours: number;
  };
}

export function BreakSegmentModal({
  segment,
  workOrder,
  isOpen,
  onClose,
  onConfirm,
  onGoOverCapacity,
  capacityWarning,
}: BreakSegmentModalProps) {
  const [parts, setParts] = useState<number[]>([]);
  const minIncrement = 0.25;

  const formatHours = (hours: number) => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}.${m.toString().padStart(2, "0")}`;
  };

  const parseHoursInput = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const [hPart, mPart] = trimmed.split(".");
    const hours = Number.parseInt(hPart || "0", 10);
    const minutes = Number.parseInt(mPart || "0", 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    const roundedMinutes = Math.max(
      0,
      Math.min(59, Math.round(minutes / 15) * 15),
    );
    const totalMinutes = hours * 60 + roundedMinutes;
    return Number((totalMinutes / 60).toFixed(2));
  };

  const adjustPartByMinutes = (index: number, deltaMinutes: number) => {
    setParts((prev) => {
      const current = prev[index] ?? minIncrement;
      const currentMinutes = Math.round(current * 60);
      const nextMinutes = Math.max(
        minIncrement * 60,
        currentMinutes + deltaMinutes,
      );
      const nextHours = Number((nextMinutes / 60).toFixed(2));
      return prev.map((p, i) => (i === index ? nextHours : p));
    });
  };

  // Build an even split in 15-minute increments
  const buildEvenSplit = (total: number, count: number) => {
    const increment = minIncrement;
    const base = Math.floor(total / count / increment) * increment;
    const remainder = total - base * count;
    const remainderSteps = Math.round(remainder / increment);
    const result = Array.from({ length: count }, (_, idx) => {
      const extra = idx < remainderSteps ? increment : 0;
      return Number((base + extra).toFixed(2));
    });
    return result;
  };

  // Reset parts when segment changes to ensure fresh calculation
  // CRITICAL: Use segment.estimatedHours (not segment.hours) for calculations
  useEffect(() => {
    if (!segment) return;
    const segmentHours = segment.estimatedHours || segment.hours || 0;
    const initialParts = buildEvenSplit(segmentHours, 2);
    setParts(initialParts);
  }, [segment?.id]);

  if (!segment || !workOrder) return null;

  // CRITICAL: Use segment.estimatedHours for all calculations and display
  const segmentHours = segment.estimatedHours || segment.hours || 0;
  const maxParts = Math.max(2, Math.floor(segmentHours / minIncrement));
  const safeParts = parts.length ? parts : buildEvenSplit(segmentHours, 2);
  const partsTotal = safeParts.reduce((sum, part) => sum + part, 0);
  const diff = Number((partsTotal - segmentHours).toFixed(2));
  const hasMismatch = Math.abs(diff) >= 0.01;
  const excessHours = capacityWarning
    ? capacityWarning.requestedHours - capacityWarning.availableHours
    : 0;

  const handleConfirm = () => {
    onConfirm(safeParts);
    // Reset to default split for next time
    const segmentHours = segment.estimatedHours || segment.hours || 0;
    setParts(buildEvenSplit(segmentHours, 2));
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={(theme) => ({
          m: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : "#181F2A",
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.contrastText
              : "#fff",
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
        // sx={{
        //   display: "flex",
        //   alignItems: "center",
        //   justifyContent: "space-between",
        //   gap: 1,
        //   fontWeight: 700,
        //   pb: 1,
        //   bgcolor: "primary.main",
        //   color: "white",
        //   px: 2.5,
        //   py: 1.5,
        // }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {capacityWarning && <AlertTriangleIcon sx={{ fontSize: "1.5rem" }} />}
          <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>
            {capacityWarning
              ? "Capacity Exceeded - Break Down Segment"
              : "Break Down Segment"}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        <Stack spacing={2}>
          {/* Capacity Warning Alert */}
          {capacityWarning && (
            <Alert severity="error">
              You're trying to assign <strong>{excessHours.toFixed(1)}h</strong>{" "}
              more than available capacity for{" "}
              <strong>{capacityWarning.technicianName}</strong> on{" "}
              <strong>{capacityWarning.day}</strong>.
            </Alert>
          )}
          {/* Segment Info */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              Segment:
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {segment.name ||
                (segment as any).DisplayText ||
                (segment as any).displayText ||
                (segment as any).segmentName ||
                `Segment ${segment.id}`}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              • Work Order: {workOrder.name || "Unknown"} •{" "}
              {workOrder.company || ""}
            </Typography>
          </Box>

          {/* Total Hours */}
          <Paper
            sx={{
              p: 2,
              textAlign: "center",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(25, 118, 210, 0.15)"
                  : "primary.light",
              border: 1,
              borderColor: "primary.main",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "white",
                fontWeight: 600,
                display: "block",
                mb: 0.5,
              }}
            >
              Segment Estimated Hours
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "white" }}>
              {formatHours(segmentHours)}h
            </Typography>
          </Paper>

          {/* Number of Parts */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Split this segment into 15-minute parts
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 1 }}
            >
              Use a suggested split or customize part durations. Total should
              match the segment hours.
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
              {[2, 3, 4, 5]
                .filter((count) => count <= maxParts)
                .map((count) => (
                  <Button
                    key={count}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setParts(buildEvenSplit(segmentHours, count))
                    }
                    sx={{ textTransform: "capitalize" }}
                  >
                    {count} parts (even)
                  </Button>
                ))}
            </Box>

            <Slider
              value={Math.min(safeParts.length || 2, maxParts)}
              onChange={(e, value) =>
                setParts(buildEvenSplit(segmentHours, value as number))
              }
              min={2}
              max={maxParts}
              valueLabelDisplay="auto"
              marks
              sx={{ mb: 1.5 }}
            />

            <Grid container spacing={2} sx={{ mb: 1.5 }}>
              <Grid item xs={4}>
                <Paper
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    bgcolor: "background.default",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      display: "block",
                    }}
                  >
                    Parts
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "text.primary" }}
                  >
                    {safeParts.length}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(25, 118, 210, 0.15)"
                        : "primary.light",
                    border: 1,
                    borderColor: "primary.main",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "white",
                      fontWeight: 600,
                      display: "block",
                    }}
                  >
                    Avg Hours/Part
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "white" }}
                  >
                    {formatHours(partsTotal / Math.max(safeParts.length, 1))}h
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper
                  sx={{
                    p: 1.5,
                    textAlign: "center",
                    bgcolor: hasMismatch
                      ? (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(211, 47, 47, 0.15)"
                            : "error.light"
                      : "background.default",
                    border: 1,
                    borderColor: hasMismatch ? "error.main" : "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      display: "block",
                    }}
                  >
                    Parts Total
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: hasMismatch ? "error.main" : "text.primary",
                    }}
                  >
                    {formatHours(partsTotal)}h
                  </Typography>
                  {hasMismatch && (
                    <Typography
                      variant="caption"
                      sx={{ color: "error.main", display: "block", mt: 0.5 }}
                    >
                      Should be {formatHours(segmentHours)}h
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              {safeParts.map((part, index) => (
                <Grid item xs={12} sm={4} key={index} sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      alignItems: "center",
                      flexWrap: "nowrap",
                      width: "100%",
                    }}
                  >
                    <TextField
                      label={`Part ${index + 1}`}
                      type="text"
                      size="small"
                      fullWidth
                      value={formatHours(part)}
                      inputProps={{ inputMode: "decimal" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Box
                              sx={{ display: "flex", flexDirection: "column" }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => adjustPartByMinutes(index, 15)}
                                sx={{ p: 0.25 }}
                              >
                                <KeyboardArrowUp fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => adjustPartByMinutes(index, -15)}
                                sx={{ p: 0.25 }}
                              >
                                <KeyboardArrowDown fontSize="small" />
                              </IconButton>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      onChange={(e) => {
                        const parsed = parseHoursInput(e.target.value);
                        if (parsed === null) return;
                        const clamped = Math.max(minIncrement, parsed);
                        const rounded =
                          Math.round(clamped / minIncrement) * minIncrement;
                        setParts((prev) =>
                          prev.map((p, i) =>
                            i === index ? Number(rounded.toFixed(2)) : p,
                          ),
                        );
                      }}
                      helperText="Format: H.MM (15 min)"
                    />
                    {safeParts.length > 2 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setParts((prev) => prev.filter((_, i) => i !== index))
                        }
                        sx={{ flexShrink: 0, bottom: "10px" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            {safeParts.length < maxParts && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setParts((prev) => [...prev, minIncrement])}
                sx={{ textTransform: "capitalize" }}
              >
                Add Part
              </Button>
            )}

            {hasMismatch && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Total parts hours ({formatHours(partsTotal)}h) does not match
                segment estimated hours ({formatHours(segmentHours)}h).
                Difference: {formatHours(Math.abs(diff))}h. Please adjust the
                parts to match the segment hours.
              </Alert>
            )}
          </Box>

          {/* Preview */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Preview
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap", gap: 1 }}
            >
              {safeParts.map((part, i) => (
                <Chip
                  key={i}
                  label={`Part ${i + 1}: ${formatHours(part)}h`}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "capitalize", fontWeight: 600 }}
        >
          Cancel
        </Button>
        {capacityWarning && (
          <Button
            onClick={onGoOverCapacity}
            variant="outlined"
            color="warning"
            sx={{ textTransform: "capitalize", fontWeight: 600 }}
          >
            Go Over Capacity
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          sx={{ textTransform: "capitalize", fontWeight: 600 }}
        >
          Break Down
        </Button>
      </DialogActions>
    </Dialog>
  );
}
